import React, { useState, useEffect } from 'react';
import { 
  Button, 
  TextField, 
  Box, 
  Typography, 
  Alert, 
  Grid, 
  Paper, 
  Container,
  InputAdornment,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Person, 
  Lock,
  Login as LoginIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { api } from '../api/client';
import { forceClearAuth } from '../utils/auth';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotTab, setForgotTab] = useState(0);
  const [fpUsernameOrEmail, setFpUsernameOrEmail] = useState('');
  const [fpPhone, setFpPhone] = useState('');
  const [fpOtp, setFpOtp] = useState('');
  const [fpNewPassword, setFpNewPassword] = useState('');
  const [fpTransactionId, setFpTransactionId] = useState('');
  const [fpMessage, setFpMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [smsResetStep, setSmsResetStep] = useState(1); // 1: Phone, 2: OTP, 3: New Password

  // Login OTP (fully verified accounts)
  const [loginStep, setLoginStep] = useState<'credentials' | 'otp'>('credentials');
  const [loginOtp, setLoginOtp] = useState('');
  const [loginTransactionId, setLoginTransactionId] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const clearLoginOtpChallenge = () => {
    try {
      sessionStorage.removeItem('loginOtpChallenge');
    } catch {
      // ignore
    }
  };

  const persistLoginOtpChallenge = (challenge: {
    transactionId: string;
    maskedEmail?: string;
    maskedPhone?: string;
    message?: string;
  }) => {
    try {
      sessionStorage.setItem('loginOtpChallenge', JSON.stringify(challenge));
    } catch {
      // ignore
    }
  };

  const enterOtpStep = (challenge: {
    transactionId: string;
    maskedEmail?: string;
    maskedPhone?: string;
    message?: string;
  }) => {
    setLoginTransactionId(challenge.transactionId || '');
    setMaskedEmail(challenge.maskedEmail || '');
    setMaskedPhone(challenge.maskedPhone || '');
    setLoginOtp('');
    setLoginStep('otp');
    setOtpResendCooldown(30);
    setError('');
    setSuccess(
      challenge.message ||
        'A verification code has been sent. Please enter it to complete sign-in.'
    );
    persistLoginOtpChallenge(challenge);
  };

  // Clear stale tokens once on mount; restore in-progress OTP challenge if present
  useEffect(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('user_info');

    const locationState = location.state as any;
    if (locationState?.verified && locationState?.message) {
      setSuccess(locationState.message);
    }

    const storedError = sessionStorage.getItem('loginError');
    if (storedError) {
      setError(storedError);
      sessionStorage.removeItem('loginError');
    }

    try {
      const raw = sessionStorage.getItem('loginOtpChallenge');
      if (raw) {
        const challenge = JSON.parse(raw);
        if (challenge?.transactionId) {
          setLoginTransactionId(challenge.transactionId);
          setMaskedEmail(challenge.maskedEmail || '');
          setMaskedPhone(challenge.maskedPhone || '');
          setLoginStep('otp');
          setSuccess(
            challenge.message ||
              'A verification code has been sent. Please enter it to complete sign-in.'
          );
        }
      }
    } catch {
      clearLoginOtpChallenge();
    }
    // Only on initial mount — do not re-run on every location identity change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (otpResendCooldown <= 0) return;
    const timer = setTimeout(() => setOtpResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [otpResendCooldown]);

  const completeLogin = (data: any) => {
    clearLoginOtpChallenge();
    setLoginStep('credentials');
    setSuccess('Login successful!');

    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);

    if (data.user) {
      localStorage.setItem('user_info', JSON.stringify(data.user));
      window.dispatchEvent(new Event('userInfoChanged'));
    }

    try {
      const { jwtDecode } = require('jwt-decode');
      const decoded: any = jwtDecode(data.access_token);

      const realmRoles = decoded.realm_access?.roles || [];
      const resourceRoles: string[] = [];

      if (decoded.resource_access) {
        Object.values(decoded.resource_access).forEach((client: any) => {
          if (client.roles) {
            resourceRoles.push(...client.roles);
          }
        });
      }

      const allRoles = [...realmRoles, ...resourceRoles];

      const adminRoles = [
        'ADMINISTRATOR', 'ROLE_ADMINISTRATOR',
        'ACCOUNTING_ASSISTANT', 'ROLE_ACCOUNTING_ASSISTANT',
        'ASSISTANT_ACCOUNTANT', 'ROLE_ASSISTANT_ACCOUNTANT',
        'CEO', 'ROLE_CEO',
        'FINANCE_MANAGER', 'ROLE_FINANCE_MANAGER',
        'OPERATIONS_MANAGER', 'ROLE_OPERATIONS_MANAGER',
        'OPERATIONS', 'ROLE_OPERATIONS',
        'FINANCE_DIRECTOR', 'ROLE_FINANCE_DIRECTOR',
        'OPERATIONS_DIRECTOR', 'ROLE_OPERATIONS_DIRECTOR',
        'RISK', 'ROLE_RISK',
        'AUDITOR', 'ROLE_AUDITOR',
        'AUDIT', 'ROLE_AUDIT'
      ];

      const hasAdminRole = allRoles.some((role: string) =>
        adminRoles.includes(role.toUpperCase())
      );

      if (hasAdminRole) {
        navigate('/admin');
      } else {
        navigate('/home');
      }
    } catch (roleError) {
      console.error('Error checking user roles:', roleError);
      navigate('/home');
    }
  };

  const parseLoginPayload = (raw: any) => {
    if (raw == null) return {};
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return {};
      }
    }
    return raw;
  };

  const isOtpChallenge = (data: any) => {
    if (!data || typeof data !== 'object') return false;
    if (data.access_token) return false;
    const flag = data.requiresOtp;
    const requiresOtp =
      flag === true || flag === 'true' || flag === 1 || flag === '1';
    return Boolean(requiresOtp && data.transactionId);
  };

  const handleLogin = async (e: React.FormEvent) => {
    try {
      e.preventDefault();
    } catch (err) {
      // fallback: if e is not a real event, ignore
    }
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const response = await api.post('/api/auth/login', { username, password });
      const data = parseLoginPayload(response.data);

      if (response.status === 200 && isOtpChallenge(data)) {
        enterOtpStep({
          transactionId: String(data.transactionId),
          maskedEmail: data.maskedEmail || '',
          maskedPhone: data.maskedPhone || '',
          message: data.message
        });
      } else if (response.status === 200 && data.access_token) {
        completeLogin(data);
      } else {
        const errMsg = data.error_description || data.error || data.message || JSON.stringify(data) || 'Login failed. Please try again.';
        setError(errMsg);
        sessionStorage.setItem('loginError', errMsg);
        setTimeout(() => setError(''), 8000);
      }
    } catch (err: any) {
      let errorMsg = 'Login failed. Please try again.';
      
      if (err.response) {
        const errorData = parseLoginPayload(err.response.data);
        if (errorData && Object.keys(errorData).length > 0) {
          // Backend may still return an OTP challenge with a non-200 in edge cases
          if (isOtpChallenge(errorData)) {
            enterOtpStep({
              transactionId: String(errorData.transactionId),
              maskedEmail: errorData.maskedEmail || '',
              maskedPhone: errorData.maskedPhone || '',
              message: errorData.message
            });
            return;
          }
          errorMsg = errorData.error_description || errorData.error || errorData.message || errorMsg;
        } else {
          errorMsg = `Login failed with status ${err.response.status}. Please check your credentials and try again.`;
        }
      } else if (err.request) {
        errorMsg = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else {
        errorMsg = err.message || errorMsg;
      }
      
      console.error('[LOGIN ERROR]', err);
      setError(errorMsg);
      sessionStorage.setItem('loginError', errorMsg);
      setTimeout(() => setError(''), 8000);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyLoginOtp = async (e: React.FormEvent) => {
    try {
      e.preventDefault();
    } catch {
      // ignore
    }
    setError('');
    setSuccess('');
    if (!loginOtp.trim() || loginOtp.trim().length !== 6) {
      setError('Please enter the 6-digit verification code from your email or SMS.');
      return;
    }
    if (!loginTransactionId) {
      setError('Verification session expired. Please sign in again.');
      setLoginStep('credentials');
      clearLoginOtpChallenge();
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/api/auth/login/verify-otp', {
        transactionId: loginTransactionId,
        otp: loginOtp.trim()
      });
      const data = parseLoginPayload(response.data);
      if (response.status === 200 && data.access_token) {
        completeLogin(data);
      } else {
        const errMsg = data.error_description || data.error || data.message || 'Verification failed. Please try again.';
        setError(errMsg);
      }
    } catch (err: any) {
      const errorData = parseLoginPayload(err?.response?.data);
      const errorMsg = errorData?.error_description || errorData?.error || errorData?.message || 'Verification failed. Please try again.';
      setError(errorMsg);
      if (errorData?.error === 'OTP_EXPIRED' || errorData?.error === 'TOO_MANY_ATTEMPTS') {
        setLoginStep('credentials');
        setLoginOtp('');
        setLoginTransactionId('');
        clearLoginOtpChallenge();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendLoginOtp = async () => {
    if (otpResendCooldown > 0 || loading) return;
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/api/auth/login/resend-otp', {
        transactionId: loginTransactionId
      });
      const data = parseLoginPayload(response.data);
      setSuccess(data?.message || 'A new verification code has been sent.');
      if (data?.maskedEmail) setMaskedEmail(data.maskedEmail);
      if (data?.maskedPhone) setMaskedPhone(data.maskedPhone);
      persistLoginOtpChallenge({
        transactionId: loginTransactionId,
        maskedEmail: data?.maskedEmail || maskedEmail,
        maskedPhone: data?.maskedPhone || maskedPhone,
        message: data?.message
      });
      setOtpResendCooldown(30);
    } catch (err: any) {
      const errorData = parseLoginPayload(err?.response?.data);
      const errorMsg = errorData?.error_description || errorData?.error || errorData?.message || 'Failed to resend code. Please try again.';
      setError(errorMsg);
      if (errorData?.error === 'OTP_EXPIRED' || errorData?.error === 'INVALID_SESSION' || errorData?.error === 'RESEND_LIMIT') {
        setLoginStep('credentials');
        setLoginOtp('');
        setLoginTransactionId('');
        clearLoginOtpChallenge();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setLoginStep('credentials');
    setLoginOtp('');
    setLoginTransactionId('');
    setMaskedEmail('');
    setMaskedPhone('');
    setError('');
    setSuccess('');
    clearLoginOtpChallenge();
  };

  return (
          <Box 
        sx={{ 
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
      <Container maxWidth="sm">
        <Paper 
          elevation={24} 
          sx={{ 
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          {/* SMEDCO Logo and Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box 
              component="img"
              src="/logo.png"
              alt="SMEDCO Logo"
              sx={{ 
                height: 80,
                width: 'auto',
                mb: 2,
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
              }}
            />
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              {loginStep === 'otp' ? 'Verify Sign-In' : 'Welcome Back'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
              {loginStep === 'otp'
                ? `Enter the code sent to ${[maskedPhone, maskedEmail].filter(Boolean).join(' / ') || 'your phone or email'}`
                : 'Sign in to your SMEDCO account'}
            </Typography>
          </Box>

          {/* Alerts */}
          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-icon': { fontSize: '1.5rem' }
              }}
            >
              {success}
            </Alert>
          )}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-icon': { fontSize: '1.5rem' }
              }}
            >
              {error}
            </Alert>
          )}

          {/* Login Form — credentials */}
          {loginStep === 'credentials' && (
          <form onSubmit={handleLogin}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Username"
                  fullWidth
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  autoFocus
                  variant="outlined"
                  size="large"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  variant="outlined"
                  size="large"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: 'text.secondary' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={<LoginIcon />}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    boxShadow: '0 8px 25px rgba(30, 60, 114, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #152a5a 0%, #1e3c72 100%)',
                      boxShadow: '0 12px 35px rgba(30, 60, 114, 0.4)',
                      transform: 'translateY(-2px)',
                    },
                    '&:disabled': {
                      background: 'linear-gradient(135deg, #8fa3c7 0%, #9bb3d1 100%)',
                      transform: 'none',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ textAlign: 'right', mt: 1 }}>
                  <Button variant="text" size="small" onClick={() => setForgotOpen(true)}>
                    Forgot password?
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
          )}

          {/* Login Form — email OTP for fully verified accounts */}
          {loginStep === 'otp' && (
          <form onSubmit={handleVerifyLoginOtp}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Verification Code"
                  fullWidth
                  value={loginOtp}
                  onChange={e => setLoginOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoFocus
                  variant="outlined"
                  size="large"
                  placeholder="6-digit code"
                  inputProps={{ inputMode: 'numeric', maxLength: 6, style: { letterSpacing: '0.4em', textAlign: 'center', fontSize: '1.25rem' } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || loginOtp.length !== 6}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    boxShadow: '0 8px 25px rgba(30, 60, 114, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #152a5a 0%, #1e3c72 100%)',
                      boxShadow: '0 12px 35px rgba(30, 60, 114, 0.4)',
                      transform: 'translateY(-2px)',
                    },
                    '&:disabled': {
                      background: 'linear-gradient(135deg, #8fa3c7 0%, #9bb3d1 100%)',
                      transform: 'none',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {loading ? 'Verifying...' : 'Verify & Sign In'}
                </Button>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button variant="text" size="small" onClick={handleBackToCredentials} disabled={loading}>
                    Back
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleResendLoginOtp}
                    disabled={loading || otpResendCooldown > 0}
                  >
                    {otpResendCooldown > 0 ? `Resend in ${otpResendCooldown}s` : 'Resend code'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
          )}

          {/* Divider */}
          {loginStep === 'credentials' && (
          <>
          <Divider sx={{ my: 4, opacity: 0.6 }}>
            <Typography variant="body2" color="text.secondary">
              New to SMEDCO?
            </Typography>
          </Divider>

          {/* Registration Link */}
          <Box sx={{ textAlign: 'center' }}>
            <Button
              component={Link}
              to="/register"
              variant="outlined"
              size="large"
              fullWidth
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  backgroundColor: 'primary.50',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Create New Account
            </Button>
          </Box>
          </>
          )}

          {/* Footer */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" color="text.secondary">
              © 2024 SMEDCO. All rights reserved.
            </Typography>
          </Box>
        </Paper>

        {/* Forgot Password Dialog */}
        <Dialog 
          open={forgotOpen} 
          onClose={() => {
            setForgotOpen(false);
            // Reset SMS reset flow when dialog closes
            setSmsResetStep(1);
            setFpPhone('');
            setFpOtp('');
            setFpNewPassword('');
            setFpMessage(null);
          }} 
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>Reset your password</DialogTitle>
          <DialogContent>
            <Tabs 
              value={forgotTab} 
              onChange={(_, v) => {
                setFpMessage(null);
                setForgotTab(v);
                // Reset SMS reset flow when switching tabs
                if (v === 1) {
                  setSmsResetStep(1);
                  setFpPhone('');
                  setFpOtp('');
                  setFpNewPassword('');
                }
              }} 
              sx={{ mb: 2 }}
            >
              <Tab label="Email" />
              <Tab label="SMS" />
            </Tabs>

            {fpMessage && (
              <Alert 
                severity={fpMessage.type === 'error' ? 'error' : fpMessage.type === 'success' ? 'success' : 'info'} 
                variant={fpMessage.type === 'error' ? 'filled' : 'standard'}
                sx={{ 
                  mb: 2, 
                  mt: 0,
                  fontSize: '0.95rem',
                  fontWeight: fpMessage.type === 'error' ? 600 : 'normal',
                  '& .MuiAlert-message': {
                    width: '100%',
                    fontWeight: 'inherit',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiAlert-icon': {
                    fontSize: '1.5rem',
                    marginRight: 1
                  }
                }}
                onClose={() => setFpMessage(null)}
              >
                {fpMessage.text}
              </Alert>
            )}

            {forgotTab === 0 && (
              <Box>
                <TextField
                  fullWidth
                  label="Username or Email"
                  value={fpUsernameOrEmail}
                  onChange={e => setFpUsernameOrEmail(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Typography variant="body2" color="text.secondary">
                  We will send a reset link to your email address if it is associated with an account.
                </Typography>
              </Box>
            )}

            {forgotTab === 1 && (
              <Box>
                {/* Step 1: Phone Number */}
                {smsResetStep === 1 && (
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={fpPhone}
                    onChange={e => setFpPhone(e.target.value)}
                    placeholder="e.g. 0771234567"
                    disabled={loading}
                  />
                )}
                
                {/* Step 2: OTP (after SMS is sent) */}
                {smsResetStep === 2 && (
                  <TextField
                    fullWidth
                    label="OTP"
                    value={fpOtp}
                    onChange={e => setFpOtp(e.target.value)}
                    placeholder="6-digit code"
                    disabled={loading}
                    sx={{ mb: 2 }}
                  />
                )}
                
                {/* Step 3: New Password (after OTP is verified) */}
                {smsResetStep === 3 && (
                  <>
                    <TextField
                      fullWidth
                      label="OTP"
                      value={fpOtp}
                      disabled
                      sx={{ mb: 2 }}
                      helperText="OTP verified ✓"
                    />
                    <TextField
                      fullWidth
                      label="New Password"
                      type="password"
                      value={fpNewPassword}
                      onChange={e => setFpNewPassword(e.target.value)}
                      disabled={loading}
                      placeholder="Enter your new password"
                    />
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setForgotOpen(false)}>Close</Button>
            {forgotTab === 0 ? (
              <Button
                variant="contained"
                onClick={async () => {
                  setFpMessage(null);
                  if (!fpUsernameOrEmail.trim()) {
                    setFpMessage({ type: 'error', text: 'Please enter your username or email' });
                    return;
                  }
                  setLoading(true);
                  try {
                    const response = await api.post('/api/auth/forgot-password/email', { usernameOrEmail: fpUsernameOrEmail.trim() });
                    const message = response?.data?.message || 'If the account exists, a password reset email has been sent.';
                    setFpMessage({ type: 'success', text: message });
                  } catch (e: any) {
                    const status = e?.response?.status;
                    const errorData = e?.response?.data;
                    const errorMsg = errorData?.message || errorData?.error || e?.message;
                    
                    if (status && status >= 400) {
                      if (status === 404 || (errorMsg && errorMsg.toLowerCase().includes('not found'))) {
                        setFpMessage({ 
                          type: 'info', 
                          text: 'If an account exists with this email address, a password reset link has been sent. Please check your inbox.' 
                        });
                      } else {
                        setFpMessage({ 
                          type: 'error', 
                          text: errorMsg || 'Failed to send reset email. Please try again.' 
                        });
                      }
                    } else {
                      setFpMessage({ 
                        type: 'error', 
                        text: 'An error occurred. Please try again.' 
                      });
                    }
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Send reset email
              </Button>
            ) : (
              <>
                {/* Step 1: Send OTP button */}
                {smsResetStep === 1 && (
                  <Button
                    variant="contained"
                    onClick={async (e) => {
                      if (loading) {
                        e.preventDefault();
                        return;
                      }
                      
                      setFpMessage(null);
                      if (!fpPhone.trim()) {
                        setFpMessage({ type: 'error', text: 'Please enter your phone number' });
                        return;
                      }
                      setLoading(true);
                      try {
                        const response = await api.post('/api/auth/forgot-password/sms/request', { phone: fpPhone.trim() });
                        setFpTransactionId(response.data.transactionId || '');
                        setFpMessage({ type: 'success', text: 'OTP has been sent to your phone number.' });
                        setSmsResetStep(2);
                      } catch (e: any) {
                        const status = e?.response?.status;
                        const errorData = e?.response?.data;
                        const errorMsg = errorData?.message || errorData?.error || e?.message;
                        
                        if (status === 404 || (errorMsg && (errorMsg.toLowerCase().includes('not found') || errorMsg.toLowerCase().includes('no account')))) {
                          setFpMessage({ 
                            type: 'error', 
                            text: errorMsg || 'No account found with this phone number. Please check your phone number and try again.' 
                          });
                        } else {
                          setFpMessage({ 
                            type: 'error', 
                            text: errorMsg || 'Failed to send OTP. Please try again.' 
                          });
                        }
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send OTP'}
                  </Button>
                )}
                
                {/* Step 2: Verify OTP button */}
                {smsResetStep === 2 && (
                  <>
                    <Button
                      onClick={() => {
                        setSmsResetStep(1);
                        setFpOtp('');
                        setFpMessage(null);
                      }}
                      disabled={loading}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={async () => {
                        setFpMessage(null);
                        if (!fpOtp.trim()) {
                          setFpMessage({ type: 'error', text: 'Please enter the OTP' });
                          return;
                        }
                        setLoading(true);
                        try {
                          await api.post('/api/auth/forgot-password/sms/verify', {
                            phone: fpPhone.trim(),
                            otp: fpOtp.trim()
                          });
                          setFpMessage({ type: 'success', text: 'OTP verified successfully. Please enter your new password.' });
                          setSmsResetStep(3);
                        } catch (e: any) {
                          const msg = e?.response?.data?.message || 'Invalid OTP. Please try again.';
                          setFpMessage({ type: 'error', text: msg });
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                    >
                      {loading ? 'Verifying...' : 'Verify OTP'}
                    </Button>
                  </>
                )}
                
                {/* Step 3: Update Password button */}
                {smsResetStep === 3 && (
                  <>
                    <Button
                      onClick={() => {
                        setSmsResetStep(2);
                        setFpNewPassword('');
                        setFpMessage(null);
                      }}
                      disabled={loading}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={async () => {
                        setFpMessage(null);
                        if (!fpNewPassword || fpNewPassword.length < 6) {
                          setFpMessage({ type: 'error', text: 'Password must be at least 6 characters' });
                          return;
                        }
                        setLoading(true);
                        try {
                          await api.post('/api/auth/forgot-password/sms/verify', {
                            phone: fpPhone.trim(),
                            otp: fpOtp.trim(),
                            newPassword: fpNewPassword
                          });
                          setFpMessage({ type: 'success', text: 'Password updated successfully. You can now sign in.' });
                          setTimeout(() => {
                            setForgotOpen(false);
                            setSmsResetStep(1);
                            setFpPhone('');
                            setFpOtp('');
                            setFpNewPassword('');
                            setFpMessage(null);
                          }, 2000);
                        } catch (e: any) {
                          const msg = e?.response?.data?.message || 'Failed to update password. Please try again.';
                          setFpMessage({ type: 'error', text: msg });
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                    >
                      {loading ? 'Updating...' : 'Update Password'}
                    </Button>
                  </>
                )}
              </>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Login;
